import dateFnsFormat from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { colors } from '@/styles';
import { enumerateWarehouseBins } from '@/utilities/warehouseRacks';
import { COUNT_MODES } from './constants';

const DISPOSITION_GREEN = colors.greens.primary;
const DISPOSITION_RED = colors.reds.cancelled;

// Keys an inventory item by its physical bin so callers can look items up by
// location instead of rescanning the whole inventory list per cycle count.
function inventoryKey(warehouseId, location) {
    return `${warehouseId}::${location}`;
}

export function buildInventoryLocationIndex(inventoryItems) {
    const index = new Map();
    (inventoryItems || []).forEach((item) => {
        const pallet = item?.pallet;
        if (!pallet?.warehouse_id || !pallet?.warehouse_location) return;
        const key = inventoryKey(pallet.warehouse_id, pallet.warehouse_location);
        const bucket = index.get(key);
        if (bucket) bucket.push(item);
        else index.set(key, [item]);
    });
    return index;
}

function cycleCountLocations(cycleCount) {
    return new Set(
        (cycleCount?.count_locations || [])
            .map((location) => location?.warehouse_location)
            .filter(Boolean)
    );
}

function locationMatches(left, right) {
    if (!left || !right) return false;
    return String(left).trim().toUpperCase() === String(right).trim().toUpperCase();
}

export function formatCycleCountDate(dateValue) {
    if (!dateValue) return '-';
    const parsed = parseISO(dateValue);
    if (Number.isNaN(parsed.getTime())) return '-';
    return dateFnsFormat(parsed, 'MMM d, yyyy');
}

export function formatCycleCountDateTime(dateValue) {
    if (!dateValue) return '-';
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return '-';
    return dateFnsFormat(parsed, 'MMM d, yyyy h:mm a');
}

export function formatShipperLabel(cycleCount) {
    const shippers = cycleCount?.shippers || [];
    if (shippers.length === 0) return '-';
    if (shippers.length === 1) {
        return shippers[0]?.shipper?.business_name || 'Unnamed Shipper';
    }
    return 'Multiple';
}

function countExpectedItemsForCount(cycleCount, index) {
    if (!cycleCount?.warehouse_id) return 0;
    let count = 0;
    cycleCountLocations(cycleCount).forEach((location) => {
        count += index.get(inventoryKey(cycleCount.warehouse_id, location))?.length || 0;
    });
    return count;
}

export function computeCycleCountExpectedItemCount(cycleCount, inventoryItems) {
    return countExpectedItemsForCount(cycleCount, buildInventoryLocationIndex(inventoryItems));
}

export function enrichCycleCountsWithExpectedItems(cycleCounts, inventoryItems) {
    const index = buildInventoryLocationIndex(inventoryItems);
    return (cycleCounts || []).map((cycleCount) => ({
        ...cycleCount,
        expectedItemCount: countExpectedItemsForCount(cycleCount, index),
    }));
}

export function getWarehouseLocationOptions(warehouseRacks = []) {
    return enumerateWarehouseBins(warehouseRacks).map((bin) => ({
        label: bin.label,
        value: bin.label,
    }));
}

// Matches mobile's calendarDateToIsoString so cycle_counts.date reads
// identically regardless of which app created the row.
export function calendarDateToIsoString(date) {
    return dateFnsFormat(date, 'yyyy-MM-dd');
}

export function filterCycleCounts(counts, inventoryItems, { searchTerm, warehouseId, dateFrom } = {}) {
    if (!counts) return counts;
    let result = counts;

    if (warehouseId) {
        result = result.filter((c) => c.warehouse_id === warehouseId);
    }

    if (dateFrom) {
        const dateFromMs = new Date(dateFrom).setHours(0, 0, 0, 0);
        if (!Number.isNaN(dateFromMs)) {
            result = result.filter((c) => {
                if (!c.date) return false;
                const countDateMs = parseISO(c.date).getTime();
                return !Number.isNaN(countDateMs) && countDateMs >= dateFromMs;
            });
        }
    }

    const trimmedTerm = (searchTerm || '').trim().toLowerCase();
    if (trimmedTerm) {
        const index = buildInventoryLocationIndex(inventoryItems);
        const itemMatchesTerm = (item) => {
            if ((item?.sku || '').toLowerCase().includes(trimmedTerm)) return true;
            const order = item?.order;
            if (String(order?.order_number || '').toLowerCase().includes(trimmedTerm)) return true;
            if ((order?.dropoff_name || '').toLowerCase().includes(trimmedTerm)) return true;
            return (order?.order_shipper?.business_name || '').toLowerCase().includes(trimmedTerm);
        };

        result = result.filter((count) => {
            const shipperJunctionMatch = (count.shippers || []).some((entry) =>
                (entry?.shipper?.business_name || '').toLowerCase().includes(trimmedTerm)
            );
            if (shipperJunctionMatch) return true;

            const locations = cycleCountLocations(count);
            if (locations.size === 0) return false;

            for (const location of locations) {
                const bucket = index.get(inventoryKey(count.warehouse_id, location)) || [];
                if (bucket.some(itemMatchesTerm)) return true;
            }
            return false;
        });
    }

    return result;
}

export function isEntireWarehouseCount(cycleCount, warehouseRacks) {
    const countLocations = cycleCountLocations(cycleCount);
    const allLocations = getWarehouseLocationOptions(warehouseRacks).map((o) => o.value);
    if (allLocations.length === 0 || countLocations.size !== allLocations.length) return false;
    return allLocations.every((loc) => countLocations.has(loc));
}

export function getCycleCountTypeLabel(cycleCount) {
    if (cycleCount?.shippers?.length) return 'Shipper';
    if (isEntireWarehouseCount(cycleCount, cycleCount?.warehouse?.warehouse_racks)) {
        return 'Entire Warehouse';
    }
    return 'Specific Bins';
}

export function getDisposition(expectedLocation, scannedLocation) {
    if (!expectedLocation) {
        return {
            from: 'N/A',
            to: scannedLocation || '-',
            label: 'Prev. Not Located',
            color: DISPOSITION_RED,
        };
    }
    if (locationMatches(expectedLocation, scannedLocation)) {
        return {
            from: expectedLocation,
            to: scannedLocation || '-',
            label: 'Unchanged',
            color: DISPOSITION_GREEN,
        };
    }
    return {
        from: expectedLocation,
        to: scannedLocation || '-',
        label: 'Changed',
        color: DISPOSITION_RED,
    };
}

export function computeItemCountsByLocation(cycleCount, inventoryItems) {
    const counts = new Map();
    (inventoryItems || []).forEach((item) => {
        const pallet = item?.pallet;
        if (pallet?.warehouse_id !== cycleCount?.warehouse_id) return;
        const loc = pallet?.warehouse_location;
        if (!loc) return;
        counts.set(loc, (counts.get(loc) || 0) + 1);
    });
    return counts;
}

export function getCycleCountLocationRows(countLocation, expectedItems) {
    if (!countLocation) return [];
    const scannedRows = [];
    const missingRows = [];
    const scannedPalletIds = new Set();

    (countLocation?.scans || []).forEach((scan) => {
        if (scan?.pallet_item_id) scannedPalletIds.add(String(scan.pallet_item_id));
        const disposition = getDisposition(
            scan.expected_warehouse_location,
            countLocation.warehouse_location
        );
        (scan?.pallet_item?.items || []).forEach((item) => {
            scannedRows.push({
                key: `scan-${scan.scan_id}-${item.item_id}`,
                item_id: item.item_id,
                sku: item.sku,
                description: item.description,
                po_number: item.order?.po_number,
                disposition,
            });
        });
    });

    const missingDisposition = {
        from: countLocation.warehouse_location || '-',
        to: 'Missing',
        label: null,
        color: DISPOSITION_RED,
    };
    (expectedItems || []).forEach((item) => {
        if (item?.pallet_id && !scannedPalletIds.has(String(item.pallet_id))) {
            missingRows.push({
                key: `missing-${item.item_id}`,
                item_id: item.item_id,
                sku: item.sku,
                description: item.description,
                po_number: item.order?.po_number,
                disposition: missingDisposition,
            });
        }
    });

    return [...missingRows, ...scannedRows];
}

export function formatDispositionText(disposition) {
    if (!disposition) return '-';
    const { from, to, label } = disposition;
    if (label) return `${from} → ${to} (${label})`;
    return `${from} → ${to}`;
}

// Builds the shipper Autocomplete options and a shipper -> locations lookup from
// the warehouse's inventory items.
export function deriveShipperOptions(items) {
    const shippersById = new Map();
    const locationsById = new Map();

    (items || []).forEach((item) => {
        const shipper = item?.order?.order_shipper;
        const shipperId = shipper?.client_id || item?.order?.shipper_id;
        const warehouseLocation = item?.pallet?.warehouse_location;
        if (!shipperId || !warehouseLocation) return;

        if (!shippersById.has(shipperId)) {
            shippersById.set(shipperId, {
                client_id: shipperId,
                business_name: shipper?.business_name || 'Unnamed Shipper',
            });
        }
        if (!locationsById.has(shipperId)) {
            locationsById.set(shipperId, new Set());
        }
        locationsById.get(shipperId).add(warehouseLocation);
    });

    const shipperOptions = Array.from(shippersById.values())
        .map((s) => {
            const count = locationsById.get(s.client_id)?.size || 0;
            return {
                value: s.client_id,
                label: `${s.business_name} (${count} ${count === 1 ? 'location' : 'locations'})`,
            };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

    return { shipperOptions, locationsByShipperId: locationsById };
}

// Returns a validation message for the create-cycle-count form, or '' when valid.
export function validateCycleCountCreate({
    warehouseId,
    date,
    countMode,
    selectedBins = [],
    selectedShipperIds = [],
    resolvedLocations = [],
}) {
    if (!warehouseId) return 'Select a warehouse.';
    if (!date) return 'Select a count date.';
    if (countMode === COUNT_MODES.SPECIFIC_BINS && selectedBins.length === 0) {
        return 'Select at least one bin or pool.';
    }
    if (countMode === COUNT_MODES.SPECIFIC_SHIPPERS && selectedShipperIds.length === 0) {
        return 'Select at least one shipper.';
    }
    if (resolvedLocations.length === 0) {
        return 'No warehouse locations were found for this count.';
    }
    return '';
}
